<template>
  <node-view-wrapper as="span" class="imageContainer">

    <div
      class="imageDiv"
      :class="{
        imageDivBorder: showArrows,
        resizingBotRightTopLeft: isBotLeftTopRightDiagonal === false,
        resizingBotLeftTopRight: isBotLeftTopRightDiagonal === true,
        containerIsLeftAligned: leftAligned,
        containerIsCenterAligned: centerAligned,
        containerIsRightAligned: rightAligned,
      }"
    >
      <template v-if="showArrows">
        <div
          v-if="$refs.resizableImg.width > 80 || centerAligned"
          class="square squareTopLeft"
          @mousedown="onMouseDown($event, false, false)"
        ></div>
        <div
          v-if="$refs.resizableImg.width > 80 || centerAligned"
          class="square squareTopRight"
          @mousedown="onMouseDown($event, true, true)"
        ></div>
        <div class="alignContainer">
          <i
            @click="alignLeft()"
            :id="'imageAlignLeft' + node.attrs.width"
            v-tooltip="$t('elementsTextTranslation.align')"
            class="fa fa-align-left alignSquare"
            :class="{ alignSquareColor: leftAligned }"
          ></i>
          <i
            @click="alignCenter()"
            :id="'imageAlignCenter' + node.attrs.width"
            v-tooltip="$t('elementsTextTranslation.alignCenter')"
            class="fa fa-align-center alignSquare"
            :class="{ alignSquareColor: centerAligned }"
          ></i>
          <i
            @click="alignRight()"
            :id="'imageAlignRight' + node.attrs.width"
            v-tooltip="$t('elementsTextTranslation.alignRight')"
            class="fa fa-align-right alignSquare"
            :class="{ alignSquareColor: rightAligned }"
          ></i>
        </div>
      </template>

      <div data-drag-handle>
        <img v-bind="node.attrs" ref="resizableImg" />
      </div>

      <template v-if="showArrows">
        <span
          class="sizeText"
          :class="[($refs.resizableImg.width > 80 || centerAligned) && $refs.resizableImg.height > 65 ? 'positionBig' : 'positionSmall']"
        >
          {{ $refs.resizableImg.width }} × {{ $refs.resizableImg.height }}
        </span>
        <div class="square squareBottomLeft" @mousedown="onMouseDown($event, false, true)"></div>
        <div class="square squareBottomRight" @mousedown="onMouseDown($event, true, false)"></div>
      </template>
    </div>

  </node-view-wrapper>
</template>

<script>
import { NodeViewWrapper, nodeViewProps } from "@tiptap/vue-2";
import { CENTER_ALIGNED, LEFT_ALIGNED, RIGHT_ALIGNED } from "../utils/ImageNodeHelper";

/**
 * Returns the alignment of the image based on the "style"-string set by the ResizableImage-extension using this node view.
 *
 * @param {string | null} style
 * @returns {undefined | "left" | "right" | "center"}
 */
function getAlignment(style) {
  if (style === LEFT_ALIGNED) {
    return "left";
  } else if (style === RIGHT_ALIGNED) {
    return "right";
  } else if (style === CENTER_ALIGNED) {
    return "center";
  }
  return undefined;
}

export default {
  components: {
    NodeViewWrapper,
  },

  props: nodeViewProps,

  data: function() {
    return {
      isResizing: false,
      previousX: undefined,
      isRight: false,
      isBotLeftTopRightDiagonal: undefined,
      alignment: getAlignment(this.node.attrs.style),
    };
  },

  computed: {
    showArrows: function() {
      // nodeViewProps provides "selected"
      return this.isResizing || this.selected;
    },

    leftAligned: function() {
      return this.alignment === "left";
    },

    centerAligned: function() {
      return this.alignment === "center";
    },

    rightAligned: function() {
      return this.alignment === "right";
    },
  },

  methods: {
    onMouseDown(e, isRight, isBotLeftTopRightDiagonal) {
      e.preventDefault();
      this.isResizing = true;
      this.isRight = isRight;
      this.isBotLeftTopRightDiagonal = isBotLeftTopRightDiagonal;
      window.addEventListener("mousemove", this.onMouseMove);
      window.addEventListener("mouseup", this.onMouseUp);
    },

    onMouseUp(e) {
      e.preventDefault();
      this.isResizing = false;
      this.previousX = undefined;
      this.isBotLeftTopRightDiagonal = undefined;
      window.removeEventListener("mousemove", this.onMouseMove);
      window.removeEventListener("mouseup", this.onMouseUp);
    },

    onMouseMove(e) {
      e.preventDefault();
      if (!this.isResizing) {
        return;
      }
      if (this.previousX === undefined) {
        this.previousX = e.x;
        return;
      }
      const nextX = e.x - this.previousX;
      if (nextX !== 0) {
        const width = this.$refs.resizableImg.width + (this.isRight ? nextX : -nextX);
        this.previousX = e.x;
        this.updateAttributes({ width: width });
      }
    },

    alignLeft() {
      if (this.leftAligned) {
        this.updateAttributes({ style: null });
        this.alignment = undefined;
      } else {
        this.updateAttributes({ style: LEFT_ALIGNED });
        this.alignment = "left";
      }
    },

    alignCenter() {
      if (this.centerAligned) {
        this.updateAttributes({ style: null });
        this.alignment = undefined;
      } else {
        this.updateAttributes({ style: CENTER_ALIGNED });
        this.alignment = "center";
      }
    },

    alignRight() {
      if (this.rightAligned) {
        this.updateAttributes({ style: null });
        this.alignment = undefined;
      } else {
        this.updateAttributes({ style: RIGHT_ALIGNED });
        this.alignment = "right";
      }
    },
  },
};
</script>

<style scoped>
img {
  cursor: pointer;
}
.imageDivBorder img {
  cursor: grab;
}
.resizingBotRightTopLeft,
.resizingBotRightTopLeft img {
  cursor: nwse-resize;
}
.resizingBotLeftTopRight,
.resizingBotLeftTopRight img {
  cursor: nesw-resize;
}
.imageContainer:hover {
  .hidden {
    visibility: visible !important;
  }
}
.imageContainer {
  overflow: hidden;
  position: relative;
}
.alignSquare {
  z-index: 1;
  outline: 1px solid black;
  box-sizing: border-box;
  background-color: white;
  opacity: 0.8;
  color: black;
  padding: 2px;
  font-size: 1.2rem;
  cursor: pointer;
}
.alignSquareColor {
  outline: 1px solid var(--colorActive) !important;
  color: var(--colorActive) !important;
  background-color: lightcyan !important;
}
.alignContainer {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translate(-50%);
  width: 67px;
}
.square {
  z-index: 1;
  position: absolute;
  height: 10px;
  width: 10px;
  outline: 1px solid black;
  box-sizing: border-box;
  background-color: white;
  opacity: 0.8;
  color: black;
}
.squareTopLeft {
  top: -5px;
  left: -5px;
  right: auto;
  cursor: nwse-resize;
}
.squareTopRight {
  top: -5px;
  right: -5px;
  left: auto;
  cursor: nesw-resize;
}
.squareBottomLeft {
  bottom: -5px;
  left: -5px;
  right: auto;
  cursor: nesw-resize;
}
.squareBottomRight {
  bottom: -5px;
  right: -5px;
  left: auto;
  cursor: nwse-resize;
}
.sizeText {
  position: absolute;
  font-size: 12px;
  text-align: center;
  opacity: 0.8;
  padding: 4px 8px;
  line-height: 1;
  outline: 1px solid black;
  background-color: white;
  color: black;
}
.positionBig {
  bottom: 4px;
  right: 4px;
}
.positionSmall {
  left: 50%;
  bottom: 0;
  transform: translate(-50%, calc(100% + 5px));
  width: 65px;
}
.imageDiv {
  display: inline-block;
  position: relative;
  box-sizing: border-box;
  line-height: 1;
}
.imageDivBorder {
  outline: 1px black dashed;
}
.containerIsLeftAligned {
  display: inline;
  float: left;
}
.containerIsCenterAligned {
  display: block;
}
.containerIsRightAligned {
  display: inline;
  float: right;
}
</style>
